function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
import { all, call, delay, getContext, put, race, select, take, takeEvery, takeLatest } from 'redux-saga/effects';
import filter from 'lodash/filter';
import find from 'lodash/find';
import findIndex from 'lodash/findIndex';
import forEach from 'lodash/forEach';
import get from 'lodash/get';
import head from 'lodash/head';
import isEmpty from 'lodash/isEmpty';
import includes from 'lodash/includes';
import omit from 'lodash/omit';
import pick from 'lodash/pick';
import reduce from 'lodash/reduce';
import reject from 'lodash/reject';
import sortBy from 'lodash/sortBy';
import valuesIn from 'lodash/valuesIn';
import LocalStorage from 'libs/LocalStorage';
import { LocalError } from 'helpers/errorTypes';
import { generateHash, generateSecret, encrypt, decrypt, symmetricEncrypt, symmetricDecrypt, getKeyFromPem, getPrvKeyPem, hybridDecrypt } from 'helpers/crypto';
import { decryptEncodedEncryptedHolderPassphrase } from 'helpers/wallet';
import { setHashCookie } from 'helpers/integration';
import { serializeQuery } from 'helpers/urlTools';
import AlertService from 'services/AlertService';
import ApiService from 'services/ApiService';
import KeysService from 'services/KeysService';
import OidcService from 'services/OidcService';
import OtpSession from 'services/OtpSession';
import SessionStorage from 'libs/SessionStorage';
import App from 'modules/App';
import Information from 'modules/Information';
import { dispatchError } from 'modules/App/sagas';
import * as actionTypes from './actionTypes';
import * as actions from './actions';
import * as constants from './constants';
import * as selectors from './selectors';
import messages from './messages';
function* generateSecrets(password, email) {
    const passphrase = yield select(selectors.passphrase);
    if (!passphrase) {
        throw new LocalError({
            code: 'NoPassphrase'
        });
    }
    const pcHash = yield call(generateHash, password, email);
    const encryptedPassphrase = yield call(symmetricEncrypt, passphrase, pcHash);
    const { encryptedBackupCode, encryptedRecoveryCode, encryptedRecoveryHash } = yield call(KeysService.generateRecoveryAndBackupCodes, pcHash);
    return {
        pcHash,
        secrets: {
            encryptedPassphrase,
            encryptedBackupCode,
            encryptedRecoveryCode,
            encryptedRecoveryHash
        }
    };
}
//----------------------------------------------------------------------------------------------------------------------
function* signOut() {
    try {
        yield call(OidcService.logout);
    // yield put(actions.signOutSuccess());
    } catch (err) {
        yield put(actions.signOutError(err));
        yield call(App.dispatchError, err, messages);
    }
}
function* signOutCallback({ payload }) {
    try {
        if (process.env.BROWSER) {
            const { logoutId } = payload;
            const requestUrl = '/api/Account/Logout';
            const { signOutIFrameUrl } = yield call(ApiService.regionalRequest, requestUrl, {
                method: 'POST',
                credentials: 'include',
                body: {
                    logoutId
                }
            });
            if (signOutIFrameUrl) {
                const iframe = document.createElement('iframe');
                iframe.width = 0;
                iframe.height = 0;
                iframe.src = signOutIFrameUrl;
                // document.getElementById('logout_iframe').appendChild(iframe);
                document.body.appendChild(iframe);
            }
            const mobileDashboardBlsReturnUrl = SessionStorage.getItem('mobileDashboardBlsReturnUrl');
            yield call(OidcService.logoutCallback);
            if (mobileDashboardBlsReturnUrl) {
                const externalRedirect = yield getContext('externalRedirect');
                externalRedirect(mobileDashboardBlsReturnUrl);
            } else {
                OidcService.login();
            }
        }
    } catch (err) {
        yield put(actions.signOutCallbackError(err));
    }
}
//----------------------------------------------------------------------------------------------------------------------
function* fetchIdentityScopes() {
    const requestUrl = '/api/Account/Dictionary/Scope';
    return yield call(ApiService.originalRequest, requestUrl);
}
function* findUser(email, password, scope, countryCode) {
    const requestUrl = '/api/IdentityProvider/FindUser';
    return yield call(ApiService.originalRequest, requestUrl, {
        method: 'POST',
        body: {
            email,
            password,
            scope,
            countryCode
        }
    });
}
function* chooseCountry(identities, resetPasswordIdentity, cancelActionType) {
    const countries = yield select(App.selectors.countries);
    const identityCountries = reduce(identities, (acc, identity)=>{
        const country = find(countries, (c)=>c.alpha2Code === identity.countryCode);
        if (country) acc.add(country);
        return acc;
    }, new Set());
    let chosenCountry = null;
    yield put(actions.setAvailableCountries([
        ...identityCountries
    ]));
    if (identityCountries.size > 1) {
        if (resetPasswordIdentity) yield put(actions.setResetPasswordIdentity());
        const [chooseCountryAction, cancelAction] = yield race([
            take(actionTypes.CHOOSE_COUNTRY),
            take(cancelActionType)
        ]);
        if (cancelAction) {
            return {
                chosenCountry,
                isCancelled: true
            };
        }
        chosenCountry = get(chooseCountryAction, 'payload.country');
    } else {
        chosenCountry = identityCountries.values().next().value;
    }
    return {
        chosenCountry,
        isCancelled: false
    };
}
function* chooseScope(countryIdentities, identityScopes, resetPasswordIdentity, cancelActionType) {
    const availableScopes = reduce(countryIdentities, (acc, identity)=>{
        const scope = find(identityScopes, (identityScope)=>identityScope.scope === identity.scope);
        if (scope) acc.add(scope);
        return acc;
    }, new Set());
    let chosenScope = null;
    yield put(actions.setAvailableScopes([
        ...availableScopes
    ]));
    if (availableScopes.size > 1) {
        if (resetPasswordIdentity) yield put(actions.setResetPasswordIdentity());
        const [chooseScopeAction, cancelAction] = yield race([
            take(actionTypes.CHOOSE_SCOPE),
            take(cancelActionType)
        ]);
        if (cancelAction) {
            return {
                chosenScope,
                isCancelled: true
            };
        }
        chosenScope = get(chooseScopeAction, 'payload.scope');
    } else {
        chosenScope = availableScopes.values().next().value;
    }
    return {
        chosenScope,
        isCancelled: false
    };
}
function* getAccount() {
    const requestUrl = '/api/Account';
    return yield call(ApiService.regionalRequest, requestUrl);
}
function* getStorageKeys() {
    return yield call(ApiService.regionalRequest, '/api/Account/StorageKeys', {
        credentials: 'include'
    });
}
function* authenticate(email, password, rememberMe, identity, pcHash, returnUrl, signatureToken) {
    let keys;
    if (identity.status === constants.IDENTITY_STATUSES.AVAILABLE) {
        const passphrase = yield call(generateSecret);
        keys = yield call(KeysService.generateKeys, passphrase, pcHash);
    }
    const requestUrl = '/api/Account/Login';
    return yield call(ApiService.regionalRequest, requestUrl, {
        method: 'POST',
        credentials: 'include',
        headers: {
            'Signature-Token': signatureToken
        },
        body: {
            email,
            password,
            rememberMe,
            identity,
            pcHash,
            keys,
            returnUrl
        }
    });
}
function* getAdditionalInformationIdentities(loadedIdentity, identities) {
    const loadedIdentityRegion = yield call(ApiService.findRegionByCountry, loadedIdentity.countryCode);
    const additionalInformationIdentities = {};
    for(let i = 0; i < identities.length; i++){
        const region = yield call(ApiService.findRegionByCountry, identities[i].countryCode);
        if (region !== loadedIdentityRegion && !additionalInformationIdentities[region.name]) {
            additionalInformationIdentities[region.name] = identities[i];
        }
    }
    return valuesIn(additionalInformationIdentities);
}
function* checkContourCloudWalletEntries() {
    const walletEntriesList = yield select(selectors.walletEntriesList);
    const ccWalletEntry = walletEntriesList.find((entry)=>entry.serviceName === 'ContourCloud');
    if (!ccWalletEntry) {
        const { holderUID, holderPassphrase } = yield select(selectors.loadedIdentity);
        const holderEntry = {
            holderUID,
            holderPassphrase
        };
        const requestUrl = '/api/ContourCloud/connect';
        yield call(ApiService.regionalRequest, requestUrl, {
            method: 'POST',
            body: {
                holderEntry
            }
        });
        yield put(actions.listWalletEntries(holderEntry));
        yield take([
            actionTypes.LIST_WALLET_ENTRIES_SUCCESS,
            actionTypes.LIST_WALLET_ENTRIES_ERROR
        ]);
    }
}
function* getMe() {
    try {
        const tokenData = yield call(OidcService.getTokenData);
        if (!tokenData) {
            yield put(actions.setLoadedIdentity());
            yield put(actions.getMeSuccess());
            return;
        }
        const identityUID = get(tokenData, 'identityuid');
        const pcHash = get(tokenData, 'pc_hash');
        const account = yield call(getAccount);
        const { language } = account;
        const langCode = yield select(App.selectors.langCode);
        if (language !== langCode) {
            const { APP_LOCALE_LANGUAGES_MAP } = App.constants;
            const locale = Object.keys(APP_LOCALE_LANGUAGES_MAP).find((loc)=>APP_LOCALE_LANGUAGES_MAP[loc] === language) || language;
            yield put(App.actions.setLocale(locale));
            yield put(App.actions.fetchLocalizationResources());
        }
        const loadedIdentity = find(account.identities, {
            identityUID
        });
        if (!loadedIdentity) {
            const err = new LocalError({
                code: 'NoLoadedIdentity'
            });
            yield put(actions.getMeError(err));
            yield call(App.dispatchError, err, messages);
            yield put(actions.signOut());
            return;
        }
        account.passphrase = yield call(symmetricDecrypt, account.encryptedPassphrase, pcHash);
        const prvKeyObj = yield call(getKeyFromPem, account.encryptedPrivateKey, account.passphrase);
        loadedIdentity.holderPassphrase = decryptEncodedEncryptedHolderPassphrase(loadedIdentity.encryptedHolderPassphrase, prvKeyObj);
        yield put(actions.setLoadedIdentity(loadedIdentity));
        const additionalInformationIdentities = yield call(getAdditionalInformationIdentities, loadedIdentity, account.identities);
        const calls = [
            put(actions.listWalletEntries(loadedIdentity)),
            put(Information.actions.checkInformation(additionalInformationIdentities))
        ];
        const takes = [
            take([
                actionTypes.LIST_WALLET_ENTRIES_SUCCESS,
                actionTypes.LIST_WALLET_ENTRIES_ERROR
            ]),
            take([
                Information.actionTypes.CHECK_INFORMATION_SUCCESS,
                Information.actionTypes.CHECK_INFORMATION_ERROR
            ])
        ];
        const invitationToken = SessionStorage.getItem('invitationToken');
        if (invitationToken) {
            calls.push(put(actions.setInvitation()));
            takes.push(take([
                actionTypes.SET_INVITATION_SUCCESS,
                actionTypes.SET_INVITATION_ERROR
            ]));
        }
        if (includes([
            constants.SCOPE_NAMES.PROFESSIONAL,
            constants.SCOPE_NAMES.CLINIC_STAFF
        ], loadedIdentity.scope)) {
            calls.push(put(actions.fetchOrganizationMemberships()));
            takes.push(take([
                actionTypes.FETCH_ORGANIZATION_MEMBERSHIPS_SUCCESS,
                actionTypes.FETCH_ORGANIZATION_MEMBERSHIPS_ERROR
            ]));
        }
        yield all(calls);
        yield all(takes);
        if (loadedIdentity.identityProviderType === constants.IDENTITY_PROVIDER_TYPE.ContourCloud) {
            yield checkContourCloudWalletEntries();
        }
        const route = yield select(App.selectors.route);
        const mainUrlRoute = yield select(selectors.mainUrlRoute);
        if (!mainUrlRoute.isFinal && mainUrlRoute.name !== route.name && (!mainUrlRoute.alternates || !includes(mainUrlRoute.alternates, route.name)) || route.name === 'auth') {
            const getUrl = yield getContext('getUrl');
            const redirect = yield getContext('redirect');
            let url = SessionStorage.getItem('returnPath');
            if (!url) {
                url = getUrl(mainUrlRoute.name, mainUrlRoute.params);
            }
            redirect(url);
        }
        yield put(actions.getMeSuccess(account));
    } catch (err) {
        yield put(actions.getMeError(err));
        yield call(App.dispatchError, err, messages);
    }
}
function* listWalletEntries({ payload }) {
    try {
        const { holderUID, holderPassphrase } = payload;
        const requestUrl = '/api/Wallet/ListEntryKeys';
        const walletEntriesList = yield call(ApiService.originalRequest, requestUrl, {
            method: 'POST',
            body: {
                holderUID,
                holderPassphrase
            }
        });
        yield put(actions.listWalletEntriesSuccess(walletEntriesList));
    } catch (err) {
        yield put(actions.listWalletEntriesError(err));
        yield call(App.dispatchError, err, messages);
    }
}
function* initAuth({ payload }) {
    try {
        const { prompt, returnPath } = payload;
        if (returnPath) {
            SessionStorage.setItem('returnPath', returnPath);
        }
        yield call(OidcService.login, prompt);
    } catch (err) {
        console.error(err);
    }
}
function* makeSetNewPasswordCC(chosenIdentity) {
    yield put(actions.setResetPasswordIdentity(chosenIdentity));
    const resetPasswordCCResult = yield take([
        actionTypes.SET_NEW_PASSWORD_CC_SUCCESS,
        actionTypes.SET_NEW_PASSWORD_CC_ERROR
    ]);
    if (resetPasswordCCResult.type === actionTypes.SET_NEW_PASSWORD_CC_ERROR) {
        throw resetPasswordCCResult.error;
    }
    return resetPasswordCCResult.payload.password;
}
function* getMfaRegion(identities, lastUsedIdentity) {
    const currentRegion = yield select(App.selectors.region);
    const regions = yield call(ApiService.findRegions, identities);
    const haveRegion = regions.has(currentRegion);
    if (haveRegion) {
        return currentRegion;
    }
    if (lastUsedIdentity) {
        return yield call(ApiService.findRegionByCountry, lastUsedIdentity.countryCode);
    }
    return regions.values().next().value;
}
function* resolveEncryptedAccount(pcHash) {
    const account = yield call(ApiService.regionalRequest, '/api/Integration/Account', {
        credentials: 'include'
    });
    const passphrase = yield call(symmetricDecrypt, account.encryptedPassphrase, pcHash);
    const privateKey = yield call(getKeyFromPem, account.encryptedPrivateKey, passphrase);
    const holderEntry = {
        holderUID: account.holderUID,
        holderPassphrase: decryptEncodedEncryptedHolderPassphrase(account.encryptedHolderPassphrase, privateKey)
    };
    return {
        holderEntry,
        privateKey
    };
}
function* updatePatientProfileStorageToken(encryptedExchangeToken, encryptedReferenceKey, storageProvider, storageAccount, pcHash) {
    const { holderEntry, privateKey } = yield call(resolveEncryptedAccount, pcHash);
    const referenceKey = yield call(decrypt, encryptedReferenceKey, privateKey);
    const exchangeToken = yield call(hybridDecrypt, encryptedExchangeToken, privateKey);
    const requestURL = '/api/Account/me/PatientProfile/StorageToken';
    yield call(ApiService.regionalRequest, requestURL, {
        method: 'POST',
        credentials: 'include',
        body: {
            holderEntry,
            storageProvider,
            exchangeToken,
            referenceKey,
            storageAccount
        }
    });
}
// Update storage walletEntries for patient who had choosen storage before wallets implementation
function* walletStorageKeysLazyMigration(pcHash) {
    const storageKeys = yield call(getStorageKeys);
    // For New Patient or Patient that did not choose storage before migration.
    // Migration will take place in gco (createPatientProfile -> updatePatientProfileStorageToken)
    if (!storageKeys) return;
    const { encryptedExchangeToken, encryptedReferenceKey, storageProvider, storageAccount } = storageKeys;
    if (!encryptedExchangeToken || !encryptedReferenceKey || !storageProvider || !storageAccount) return;
    // eslint-disable-next-line max-len
    yield call(updatePatientProfileStorageToken, encryptedExchangeToken, encryptedReferenceKey, storageProvider, storageAccount, pcHash);
}
function* executeConfirmEmailOtpFlow(activationResponse) {
    const otpRegion = yield select(App.selectors.region);
    const { signatureToken } = activationResponse;
    const { signatureToken: otpSignatureToken } = yield call(OtpSession.sendOtp, signatureToken, otpRegion);
    const otpStatus = yield call(OtpSession.getOtpStatus, otpSignatureToken, otpRegion);
    yield put(actions.setOtp(otpStatus, otpRegion, otpSignatureToken));
    return yield call(OtpSession.validateOtp, otpSignatureToken, otpRegion, otpStatus.method, actionTypes.CONFIRM_EMAIL_CANCEL);
}
function* validateConfirmEmailOtpCode(email) {
    const activationRequestUrl = '/api/Account/Activation/Start';
    const activationResponse = yield call(ApiService.regionalRequest, activationRequestUrl, {
        method: 'POST',
        body: {
            email
        }
    });
    const confirmEmailType = activationResponse ? 'code' : 'email';
    yield put(actions.setConfirmEmailActivationType(confirmEmailType));
    if (confirmEmailType !== 'code') return null;
    const enterOtpResult = yield race({
        result: call(executeConfirmEmailOtpFlow, activationResponse),
        cancel: take(actionTypes.CONFIRM_EMAIL_CANCEL)
    });
    yield put(actions.clearOtp());
    if (!enterOtpResult || enterOtpResult.isCancelled) {
        return null;
    }
    const { signatureToken: verifiedOtpSignatureToken } = enterOtpResult.result;
    return verifiedOtpSignatureToken;
}
function* fetchWalletEntryKeys(holderEntry) {
    const requestUrl = '/api/Integration/Wallet/ListEntryKeys';
    return yield call(ApiService.originalRequest, requestUrl, {
        method: 'POST',
        credentials: 'include',
        body: holderEntry
    });
}
function* checkContourCloudForIntegration(password, holderEntry, walletEntryKeys) {
    const ccWalletEntry = walletEntryKeys.find((entry)=>entry.serviceName === 'ContourCloud');
    if (!ccWalletEntry) {
        const requestUrl = '/api/Integration/ContourCloud/Connect';
        yield call(ApiService.regionalRequest, requestUrl, {
            method: 'POST',
            credentials: 'include',
            body: {
                holderEntry,
                password
            }
        });
    }
}
function* checkWalletForIntegration(password, pcHash) {
    const { holderEntry } = yield call(resolveEncryptedAccount, pcHash);
    const walletEntryKeys = yield call(fetchWalletEntryKeys, holderEntry);
    yield call(checkContourCloudForIntegration, password, holderEntry, walletEntryKeys);
}
function* signIn({ payload }) {
    let chosenIdentity;
    try {
        const { email, rememberMe, returnUrl } = payload;
        let { password } = payload;
        const [identityScopes, findUserResult] = yield all([
            call(fetchIdentityScopes),
            call(findUser, email, password)
        ]);
        let { identities, lastUsedIdentity, signatureToken } = findUserResult;
        const hasIdentities = identities.length > 0;
        if (!hasIdentities) {
            const err = new LocalError({
                code: 'UserOrPasswordIncorrect'
            });
            yield put(actions.signInError(err));
            yield call(App.dispatchError, err, messages);
            return;
        }
        const hasActiveIdentity = !!identities.find((identity)=>identity.status === 'Active' || identity.status === 'ResetPassword');
        if (signatureToken && hasActiveIdentity) {
            const otpRegion = yield call(getMfaRegion, identities, lastUsedIdentity);
            const { signatureToken: otpSignatureToken } = yield call(OtpSession.sendOtp, signatureToken, otpRegion);
            const otpStatus = yield call(OtpSession.getOtpStatus, otpSignatureToken, otpRegion);
            yield put(actions.setOtp(otpStatus, otpRegion, otpSignatureToken));
            const enterOtpResult = yield call(OtpSession.validateOtp, otpSignatureToken, otpRegion, otpStatus.method, actionTypes.SIGN_IN_CANCEL);
            if (enterOtpResult.isCancelled) {
                return;
            }
            signatureToken = enterOtpResult.signatureToken;
        }
        const resetPasswordIdentity = find(identities, {
            status: 'ResetPassword'
        });
        if (resetPasswordIdentity) {
            password = yield call(makeSetNewPasswordCC, resetPasswordIdentity);
            const findUserAfterResetResult = yield call(findUser, email, password);
            identities = findUserAfterResetResult.identities;
            lastUsedIdentity = findUserAfterResetResult.lastUsedIdentity;
        }
        if (lastUsedIdentity) {
            const idx = findIndex(identities, (i)=>i.countryCode === lastUsedIdentity.countryCode && i.scope === lastUsedIdentity.scope);
            if (idx >= 0) {
                chosenIdentity = lastUsedIdentity;
            }
        }
        if (!chosenIdentity) {
            // Choose country
            const { chosenCountry, isCancelled: isChooseCountryCancelled } = yield call(chooseCountry, identities, resetPasswordIdentity, actionTypes.SIGN_IN_CANCEL);
            if (isChooseCountryCancelled) {
                return;
            }
            const countryIdentities = filter(identities, (identity)=>identity.countryCode === chosenCountry.alpha2Code);
            const { chosenScope, isCancelled: isChooseScopeCancelled } = yield call(chooseScope, countryIdentities, identityScopes, resetPasswordIdentity, actionTypes.SIGN_IN_CANCEL);
            if (isChooseScopeCancelled) {
                return;
            }
            chosenIdentity = find(countryIdentities, {
                scope: chosenScope.scope
            });
        }
        if (chosenIdentity.status === constants.IDENTITY_STATUSES.INACTIVE) {
            yield put(actions.signInError(new LocalError({
                code: 'IdentityInactive'
            }), chosenIdentity));
            const confirmEmailVerifiedSignatureToken = yield call(validateConfirmEmailOtpCode, email);
            if (!confirmEmailVerifiedSignatureToken) return;
            signatureToken = confirmEmailVerifiedSignatureToken;
        }
        const { countryCode } = chosenIdentity;
        if (countryCode) {
            yield put(App.actions.setRegion(countryCode));
            const setRegionResult = yield take([
                App.actionTypes.SET_REGION_SUCCESS,
                App.actionTypes.SET_REGION_ERROR
            ]);
            if (setRegionResult.type === App.actionTypes.SET_REGION_ERROR) {
                const err = setRegionResult.error;
                yield put(actions.signInError(err));
                yield call(App.dispatchError, err, messages);
                return;
            }
        }
        const pcHash = yield call(generateHash, password, email);
        const isIntegrationProcess = yield select(App.selectors.isIntegrationProcess);
        if (isIntegrationProcess) {
            yield call(setHashCookie, pcHash);
        }
        // eslint-disable-next-line max-len
        const response = yield call(authenticate, email, password, rememberMe, chosenIdentity, pcHash, returnUrl, signatureToken);
        const isWalletStorageMigrationRequired = get(response, 'isWalletStorageMigrationRequired', false);
        if (isWalletStorageMigrationRequired) {
            yield call(walletStorageKeysLazyMigration, pcHash);
        }
        if (isIntegrationProcess) {
            yield call(checkWalletForIntegration, password, pcHash);
        }
        const isTrustedClientFlow = yield select(App.selectors.isTrustedClientFlow);
        if (isTrustedClientFlow) {
            const { holderEntry } = yield call(resolveEncryptedAccount, pcHash);
            if (!window.TrustedClientChannel) {
                console.error('Missing TrustedClientChannel JavascriptChannel on partner application side');
            } else {
                window.TrustedClientChannel.postMessage(JSON.stringify({
                    holderPassphrase: holderEntry.holderPassphrase
                }));
            }
            SessionStorage.setItem('holderPassphrase', holderEntry.holderPassphrase);
        }
        const externalRedirect = yield getContext('externalRedirect');
        const isMobileDisplayMode = yield select(App.selectors.isMobileDisplayMode);
        if (isMobileDisplayMode) {
            const domain = yield getContext('domain');
            const externalReturnUrl = encodeURIComponent(returnUrl);
            LocalStorage.setItem('mobileSetupExternalReturnUrl', externalReturnUrl);
            externalRedirect(`${domain}/mobile/setup`);
        } else {
            externalRedirect(returnUrl);
        }
    } catch (err) {
        const businessError = err.getBusinessError && err.getBusinessError();
        if (businessError && businessError.code === 'IdentityInactive') {
            yield put(actions.signInError(err, chosenIdentity));
            return;
        }
        yield put(actions.signInError(err));
        yield call(App.dispatchError, err, messages);
    }
}
//----------------------------------------------------------------------------------------------------------------------
function* resendOtp() {
    try {
        const otpRegion = yield select(selectors.otpRegion);
        const otpSignatureToken = yield select(selectors.otpSignatureToken);
        const { signatureToken } = yield call(OtpSession.resendOtp, otpSignatureToken, otpRegion);
        const alert = yield call(AlertService.createAlertDispatcher);
        yield alert.success(messages.alerts.resendCodeSuccess);
        const otpStatus = yield call(OtpSession.getOtpStatus, signatureToken, otpRegion);
        yield put(actions.setOtp(otpStatus, otpRegion, otpSignatureToken));
        yield put(actions.resendOtpSuccess(signatureToken));
    } catch (err) {
        yield put(actions.resendOtpError(err));
        yield call(App.dispatchError, err, messages);
    }
}
//----------------------------------------------------------------------------------------------------------------------
// function* signInCallback() {
//   try {
//     yield call(OidcService.loginCallback);
//     yield put(actions.signInCallbackSuccess());
//   } catch (err) {
//     yield put(actions.signInCallbackError(err));
//     yield call(App.dispatchError, err, messages);
//   }
// }
//----------------------------------------------------------------------------------------------------------------------
function* signUp({ payload }) {
    try {
        const { email, password, invitationToken, returnUrl } = payload;
        const requestUrl = '/api/Account';
        const pcHash = yield call(generateHash, password, email);
        const passphrase = yield call(generateSecret);
        const keys = yield call(KeysService.generateKeys, passphrase, pcHash);
        const account = yield call(ApiService.regionalRequest, requestUrl, {
            method: 'POST',
            body: _object_spread_props(_object_spread({}, omit(payload, [
                'returnUrl'
            ])), {
                pcHash,
                keys
            })
        });
        const identity = head(account.identities);
        if (invitationToken && identity.status === constants.IDENTITY_STATUSES.ACTIVE || identity.status === constants.IDENTITY_STATUSES.INACTIVE) {
            yield put(actions.signIn({
                email,
                password,
                returnUrl
            }));
        }
        yield put(actions.signUpSuccess(identity));
    } catch (err) {
        const businessError = err.getBusinessError && err.getBusinessError();
        if (businessError && businessError.code === 'InvitationNotFound') {
            SessionStorage.removeItem('invitationToken');
            SessionStorage.removeItem('invitationCountryCode');
            yield put(actions.rejectInvitationSuccess());
        }
        yield put(actions.signUpError(err));
        yield call(App.dispatchError, err, messages);
    }
}
//----------------------------------------------------------------------------------------------------------------------
function* fetchIdentityProviders() {
    try {
        const requestUrl = '/api/IdentityProvider/Dictionary';
        const identityProviders = yield call(ApiService.originalRequest, requestUrl);
        yield put(actions.fetchIdentityProvidersSuccess(identityProviders));
    } catch (err) {
        yield put(actions.fetchIdentityProvidersError(err));
        yield call(App.dispatchError, err, messages);
    }
}
//----------------------------------------------------------------------------------------------------------------------
function* fetchIdentities() {
    try {
        const requestUrl = '/api/Account/Identity';
        const identities = yield call(ApiService.originalRequest, requestUrl);
        yield put(actions.fetchIdentitiesSuccess(identities));
    } catch (err) {
        yield put(actions.fetchIdentitiesError(err));
        yield call(App.dispatchError, err, messages);
    }
}
//----------------------------------------------------------------------------------------------------------------------
function* switchIdentity({ payload }) {
    try {
        const { identity, password } = payload;
        const { identityUID } = identity;
        let email;
        let pcHash;
        if (password) {
            const information = yield select(Information.selectors.information);
            email = information.email;
            pcHash = yield call(generateHash, password, email);
        } else {
            const tokenData = yield call(OidcService.getTokenData);
            pcHash = get(tokenData, 'pc_hash');
        }
        const requestUrl = '/api/Account/SwitchIdentity';
        yield call(ApiService.regionalRequest, requestUrl, {
            method: 'POST',
            credentials: 'include',
            body: {
                identityUID,
                email,
                password,
                pcHash
            }
        });
        // yield delay(1000);
        // yield call(OidcService.revokeAccessToken);
        yield put(actions.initAuth('switch-identity'));
        yield put(actions.switchIdentitySuccess());
    } catch (err) {
        yield put(actions.switchIdentityError(err));
        yield call(App.dispatchError, err, messages);
    }
}
//----------------------------------------------------------------------------------------------------------------------
function* sendConfirmEmail({ payload }) {
    try {
        const { email, password, identityProviderKey } = payload;
        const pcHash = yield call(generateHash, password, email);
        const requestUrl = '/api/Account/ConfirmEmail/Send';
        yield call(ApiService.regionalRequest, requestUrl, {
            method: 'POST',
            body: {
                identityProviderKey,
                email,
                password,
                pcHash
            }
        });
        yield put(actions.sendConfirmEmailSuccess());
    } catch (err) {
        yield put(actions.sendConfirmEmailError(err));
    }
}
//----------------------------------------------------------------------------------------------------------------------
function* updateAccountSettings({ payload }) {
    try {
        const { language } = payload;
        const requestUrl = '/api/Account/Settings';
        yield call(ApiService.regionalRequest, requestUrl, {
            method: 'PUT',
            body: {
                language
            }
        });
        yield put(actions.updateAccountSettingsSuccess(payload));
    } catch (err) {
        yield put(actions.updateAccountSettingsError(err));
    }
}
//----------------------------------------------------------------------------------------------------------------------
function* getInvitation(invitationToken, countryCode) {
    /*
  const invitation = {
    invitationCode  : '2bed3f25-8a48-4079-8db7-6e7c42ca291b',
    subject         : 'GCO.SharingRequest',
    countryCode     : 'pt',
    allowedCountries: ['cz', 'de', 'es', 'gr', 'pt'],
    allowedScopes   : ['Personal', 'Caregiver'],
    scope           : 'Caregiver',
    information     : {
      email      : 'test_hcp_pl20@mailinator.com',
      firstName  : 'Test',
      lastName   : 'HcpPl20',
      dateOfBirth: '2023-05-31T06:34:31.849Z',
    },
    inviter: {
      firstName       : 'Test',
      lastName        : 'HcpPl11',
      organizationName: 'Test2 Diabetes Center',
    },
  };
  yield delay(200);
   */ const region = yield call(ApiService.findRegionByCountry, countryCode);
    const requestUrl = `/api/Invitation?invitationToken=${invitationToken}`;
    const invitation = yield call(ApiService.crossRegionalRequest, region, requestUrl);
    return invitation;
}
function* setInvitation() {
    try {
        const invitationToken = SessionStorage.getItem('invitationToken');
        if (!invitationToken) {
            yield put(actions.setInvitationSuccess());
            return;
        }
        const invitationCountryCode = SessionStorage.getItem('invitationCountryCode');
        let invitation = yield select(selectors.invitation);
        if (!invitation) {
            invitation = yield call(getInvitation, invitationToken, invitationCountryCode);
        }
        yield put(actions.setInvitationSuccess(invitation, invitationToken));
    } catch (err) {
        yield put(actions.setInvitationError(err));
        yield call(App.dispatchError, err, messages);
        SessionStorage.removeItem('invitationToken');
        SessionStorage.removeItem('invitationCountryCode');
    }
}
//----------------------------------------------------------------------------------------------------------------------
function* fetchMfa() {
    const requestUrl = '/api/Mfa';
    return yield call(ApiService.regionalRequest, requestUrl);
}
//----------------------------------------------------------------------------------------------------------------------
function* getInvitationPrivateKey(invitation) {
    const { invitationCode, information, keys } = invitation;
    const { invitationEncryptedPassphrase, invitationEncryptedPrivateKey } = keys;
    const { email } = information;
    const invitationHash = yield call(generateHash, invitationCode, email);
    const invitationPassphrase = symmetricDecrypt(invitationEncryptedPassphrase, invitationHash);
    return getKeyFromPem(invitationEncryptedPrivateKey, invitationPassphrase);
}
function* recryptOrganizationKeys(invitation) {
    const { details } = invitation;
    const { organizationEncryptedPassphrase, organizationEncryptedPrivateKey } = details;
    const invitationPrvKeyObj = yield call(getInvitationPrivateKey, invitation);
    const organizationPassphrase = decrypt(organizationEncryptedPassphrase, invitationPrvKeyObj);
    const organizationPrvKeyObj = getKeyFromPem(organizationEncryptedPrivateKey, organizationPassphrase);
    const accountPublicKey = yield select(selectors.publicKey);
    const accountPublicKeyObj = getKeyFromPem(accountPublicKey);
    const passphrase = yield call(generateSecret);
    const encryptedPassphrase = encrypt(passphrase, accountPublicKeyObj);
    const encryptedPrivateKey = getPrvKeyPem(organizationPrvKeyObj, passphrase);
    return {
        encryptedOrganizationPassphrase: encryptedPassphrase,
        encryptedOrganizationPrivateKey: encryptedPrivateKey
    };
}
function* getAcceptInvitationDetails(invitation) {
    const { subject } = invitation;
    if (subject === 'Organization') {
        return yield call(recryptOrganizationKeys, invitation);
    }
    return null;
}
function* successSideEffect(invitation) {
    const { subject } = invitation;
    if (subject === 'Organization') {
        yield put(actions.fetchOrganizationMemberships());
        yield take([
            actionTypes.FETCH_ORGANIZATION_MEMBERSHIPS_SUCCESS,
            actionTypes.FETCH_ORGANIZATION_MEMBERSHIPS_ERROR
        ]);
    }
}
function* acceptInvitation({ payload }) {
    const holderEntry = yield select(selectors.holderEntry);
    try {
        const { invitation, identityUID } = payload;
        const { invitationCode, countryCode, subject } = invitation;
        const details = yield call(getAcceptInvitationDetails, invitation);
        const region = yield call(ApiService.findRegionByCountry, countryCode);
        const requestUrl = '/api/Invitation/accept';
        yield call(ApiService.crossRegionalRequest, region, requestUrl, {
            method: 'POST',
            body: {
                identityUID,
                invitationCode,
                subject,
                details: _object_spread_props(_object_spread({}, details), {
                    holderEntry
                })
            }
        });
        SessionStorage.removeItem('invitationToken');
        SessionStorage.removeItem('invitationCountryCode');
        yield call(successSideEffect, invitation);
        yield put(actions.acceptInvitationSuccess());
    } catch (err) {
        yield put(actions.acceptInvitationError(err));
        yield call(App.dispatchError, err, messages);
    }
}
function* acceptInvitationWithNewProfile({ payload }) {
    try {
        const { invitation, informationValues, password } = payload;
        const { countryCode, scope } = invitation;
        const mainInformation = yield select(Information.selectors.information);
        const basicInformation = pick(mainInformation, Information.constants.BASIC_INFORMATION);
        const { email } = basicInformation;
        const information = _object_spread({}, basicInformation, informationValues);
        const correctAvatar = information.avatar && information.avatar.includes('base64') ? information.avatar : mainInformation.base64Avatar;
        if (information.personalIdentifier && isEmpty(information.personalIdentifier)) {
            delete information.personalIdentifier;
        }
        const { mfa: fullMfa, signatureToken } = yield call(fetchMfa);
        const mfa = fullMfa.identifierUID ? pick(fullMfa, [
            'identifierUID',
            'identifier'
        ]) : undefined;
        let keys;
        if (password) {
            const { secrets } = yield call(generateSecrets, password, email);
            keys = secrets;
        }
        const region = yield call(ApiService.findRegionByCountry, countryCode);
        const requestUrl = '/api/Account/Identity';
        const identity = yield call(ApiService.crossRegionalRequest, region, requestUrl, {
            method: 'POST',
            headers: {
                'Signature-Token': signatureToken
            },
            body: {
                identity: {
                    countryCode,
                    scope
                },
                password,
                keys,
                information: _object_spread_props(_object_spread({}, information), {
                    avatar: correctAvatar
                }),
                mfa
            }
        });
        const currentIdentities = yield select(selectors.identities);
        const identities = sortBy([
            ...currentIdentities,
            identity
        ], [
            'countryCode',
            'scope'
        ]);
        yield put(actions.setIdentities(identities));
        const { identityUID } = identity;
        yield put(actions.acceptInvitation(invitation, identityUID));
    } catch (err) {
        yield put(actions.acceptInvitationError(err));
        yield call(App.dispatchError, err, messages);
    }
}
//----------------------------------------------------------------------------------------------------------------------
function* rejectInvitation({ payload }) {
    try {
        const { invitation } = payload;
        const { invitationCode, countryCode } = invitation;
        const region = yield call(ApiService.findRegionByCountry, countryCode);
        const requestUrl = '/api/Invitation/reject';
        yield call(ApiService.crossRegionalRequest, region, requestUrl, {
            method: 'POST',
            body: {
                invitationCode
            }
        });
        SessionStorage.removeItem('invitationToken');
        SessionStorage.removeItem('invitationCountryCode');
        yield put(actions.rejectInvitationSuccess());
    } catch (err) {
        yield put(actions.rejectInvitationError(err));
        yield call(App.dispatchError, err, messages);
    }
}
//----------------------------------------------------------------------------------------------------------------------
function getFilteredTemplate(information, informationTemplate) {
    const filteredTemplate = [];
    forEach(informationTemplate, (ft)=>{
        const value = get(information, ft.name);
        if (ft.isMandatory && !value || !includes(Information.constants.BASIC_INFORMATION, ft.name)) {
            filteredTemplate.push(ft);
        }
    });
    return filteredTemplate;
}
function* fetchInvitationInformationTemplate({ payload }) {
    try {
        const { invitation } = payload;
        const { scope, countryCode } = invitation;
        const query = serializeQuery({
            scope,
            countryCode
        });
        const requestUrl = `/api/Information/Account/Template?${query}`;
        const mainInformation = yield select(Information.selectors.information);
        const information = pick(mainInformation, Information.constants.BASIC_INFORMATION);
        const informationTemplate = yield call(ApiService.originalRequest, requestUrl);
        const filteredTemplate = yield call(getFilteredTemplate, information, informationTemplate);
        yield put(actions.fetchInvitationInformationTemplateSuccess(filteredTemplate));
    } catch (err) {
        yield put(actions.fetchInvitationInformationTemplateError(err));
        yield call(App.dispatchError, err, messages);
    }
}
//----------------------------------------------------------------------------------------------------------------------
function* decryptPassphraseByRecoveryCode(encryptedPassphrase, encryptedRecoveryHash, recoveryCode) {
    const recoveryHash = yield call(symmetricDecrypt, encryptedRecoveryHash, recoveryCode);
    return yield call(symmetricDecrypt, encryptedPassphrase, recoveryHash);
}
function* changePassword({ payload }) {
    try {
        const { oldPassword, password } = payload;
        const passphrase = yield select(selectors.passphrase);
        if (!passphrase) {
            const err = new LocalError({
                code: 'NoPassphrase'
            });
            yield put(actions.changePasswordError(err));
            yield call(App.dispatchError, err, messages);
            return;
        }
        const { email } = yield select(Information.selectors.information);
        const { pcHash, secrets } = yield call(generateSecrets, password, email);
        const requestUrl = '/api/Account/ChangePassword';
        yield call(ApiService.regionalRequest, requestUrl, {
            method: 'POST',
            credentials: 'include',
            body: _object_spread({
                oldPassword,
                password,
                pcHash
            }, secrets)
        });
        yield call(OidcService.revokeAccessToken);
        yield put(actions.changePasswordSuccess());
    } catch (err) {
        yield put(actions.changePasswordError(err));
        yield call(App.dispatchError, err, messages);
    }
}
//----------------------------------------------------------------------------------------------------------------------
function* resetPassword({ payload }) {
    try {
        const { email } = payload;
        const requestUrl = '/api/Account/ResetPassword';
        const response = yield call(ApiService.regionalRequest, requestUrl, {
            method: 'POST',
            body: {
                email
            }
        });
        const resetPasswordType = response ? 'code' : 'email';
        yield put(actions.setResetPasswordType(resetPasswordType));
        if (resetPasswordType === 'code') {
            const otpRegion = yield select(App.selectors.region);
            const { signatureToken } = response;
            const { signatureToken: otpSignatureToken } = yield call(OtpSession.sendOtp, signatureToken, otpRegion);
            const otpStatus = yield call(OtpSession.getOtpStatus, otpSignatureToken, otpRegion);
            yield put(actions.setOtp(otpStatus, otpRegion, otpSignatureToken));
            const enterOtpResult = yield call(OtpSession.validateOtp, otpSignatureToken, otpRegion, otpStatus.method, actionTypes.RESET_PASSWORD_CANCEL);
            if (enterOtpResult.isCancelled) {
                return;
            }
            const { signatureToken: verifiedSignatureToken } = enterOtpResult;
            yield put(actions.setVerifiedSignatureToken(verifiedSignatureToken));
        }
        yield put(actions.resetPasswordSuccess());
    } catch (err) {
        yield put(actions.resetPasswordError(err));
        yield call(App.dispatchError, err, messages);
    }
}
//----------------------------------------------------------------------------------------------------------------------
/**
 *  AID auth { token: string }
 *  CC auth { email: string, temporaryPassword: string }
 */ function* recoverIdentityProviderPassphrase(identityProviderKey, auth) {
    const requestUrl = '/api/IdentityProvider/ResetPassword/RecoveryHash';
    const response = yield call(ApiService.regionalRequest, requestUrl, {
        method: 'POST',
        body: {
            identityProviderKey,
            auth
        }
    });
    if (!response) {
        return {};
    }
    const { encryptedPassphrase, recoveryCode, encryptedRecoveryHash } = response;
    const passphrase = yield call(decryptPassphraseByRecoveryCode, encryptedPassphrase, encryptedRecoveryHash, recoveryCode);
    return {
        passphrase
    };
}
function* approveIdentityProviderResetPassword(identityProviderKey, auth, password, secrets = null) {
    const requestUrl = '/api/IdentityProvider/ResetPassword/Approve';
    yield call(ApiService.regionalRequest, requestUrl, {
        method: 'POST',
        body: _object_spread({
            auth,
            identityProviderKey,
            password
        }, secrets)
    });
}
function* setNewPassword({ payload }) {
    try {
        const { email } = payload;
        const token = yield select(selectors.verifiedSignatureToken);
        const auth = {
            token
        };
        const identityProviderKey = 'Aid';
        const [setPasswordAction, cancelAction] = yield race([
            take(actionTypes.SET_PASSWORD),
            take(actionTypes.SET_NEW_PASSWORD_CANCEL)
        ]);
        if (cancelAction) {
            return;
        }
        const { password } = setPasswordAction.payload;
        let passphrase;
        let secrets;
        const { passphrase: recoveredPassphrase } = yield call(recoverIdentityProviderPassphrase, identityProviderKey, auth);
        const pcHash = yield call(generateHash, password, email);
        if (!recoveredPassphrase) {
            passphrase = yield call(generateSecret);
            secrets = yield call(KeysService.generateKeys, passphrase, pcHash);
        } else {
            passphrase = recoveredPassphrase;
            const encryptedPassphrase = yield call(symmetricEncrypt, passphrase, pcHash);
            const { encryptedBackupCode, encryptedRecoveryCode, encryptedRecoveryHash } = yield call(KeysService.generateRecoveryAndBackupCodes, pcHash);
            secrets = {
                encryptedPassphrase,
                encryptedBackupCode,
                encryptedRecoveryCode,
                encryptedRecoveryHash
            };
        }
        yield call(approveIdentityProviderResetPassword, identityProviderKey, auth, password, secrets);
        yield put(actions.setNewPasswordSuccess());
    } catch (err) {
        yield put(actions.setNewPasswordError(err));
        yield call(App.dispatchError, err, messages);
    }
}
function* setNewPasswordCC({ payload }) {
    try {
        const { identityProviderKey, email, temporaryPassword, password } = payload;
        const auth = {
            email,
            temporaryPassword
        };
        const { passphrase } = yield call(recoverIdentityProviderPassphrase, identityProviderKey, auth);
        let secrets;
        if (passphrase) {
            const pcHash = yield call(generateHash, password, email);
            const encryptedPassphrase = yield call(symmetricEncrypt, passphrase, pcHash);
            const { encryptedBackupCode, encryptedRecoveryCode, encryptedRecoveryHash } = yield call(KeysService.generateRecoveryAndBackupCodes, pcHash);
            secrets = {
                encryptedPassphrase,
                encryptedBackupCode,
                encryptedRecoveryCode,
                encryptedRecoveryHash
            };
        }
        yield call(approveIdentityProviderResetPassword, identityProviderKey, auth, password, secrets);
        yield put(actions.setNewPasswordCCSuccess(payload.password));
    } catch (err) {
        yield put(actions.setNewPasswordCCError(err));
        yield call(App.dispatchError, err, messages);
    }
}
//----------------------------------------------------------------------------------------------------------------------
function* recoverPassphrase() {
    const requestUrl = '/api/Account/RecoveryHash';
    const { encryptedPassphrase, recoveryCode, encryptedRecoveryHash } = yield call(ApiService.regionalRequest, requestUrl);
    return yield call(decryptPassphraseByRecoveryCode, encryptedPassphrase, encryptedRecoveryHash, recoveryCode);
}
function* sendSecrets({ encryptedPassphrase, encryptedBackupCode, encryptedRecoveryCode, encryptedRecoveryHash }) {
    const requestUrl = '/api/Account/Secrets';
    yield call(ApiService.regionalRequest, requestUrl, {
        method: 'POST',
        body: {
            encryptedPassphrase,
            encryptedBackupCode,
            encryptedRecoveryCode,
            encryptedRecoveryHash
        }
    });
}
function* returnRedirect() {
    const blsReturnUrl = SessionStorage.getItem('blsReturnUrl');
    if (blsReturnUrl) {
        SessionStorage.removeItem('blsReturnUrl');
        const externalRedirect = yield getContext('externalRedirect');
        externalRedirect(blsReturnUrl);
    } else {
        const getUrl = yield getContext('getUrl');
        const redirect = yield getContext('redirect');
        const url = getUrl('app-catalog');
        redirect(url);
    }
}
function* passphraseRecovery() {
    try {
        const currentPassphrase = yield select(selectors.passphrase);
        if (currentPassphrase) {
            // Passphrase has been decrypted, recovery is not necessary
            yield put(actions.passphraseRecoverySuccess(currentPassphrase));
            yield call(returnRedirect);
            return;
        }
        const passphrase = yield call(recoverPassphrase);
        if (!passphrase) {
            const err = new LocalError({
                code: 'NoPassphrase'
            });
            yield put(actions.passphraseRecoveryError(err));
            yield call(App.dispatchError, err, messages);
            return;
        }
        const tokenData = yield call(OidcService.getTokenData);
        const pcHash = get(tokenData, 'pc_hash');
        if (!pcHash) {
            const err = new LocalError({
                code: 'NoPCHash'
            });
            yield put(actions.passphraseRecoveryError(err));
            yield call(App.dispatchError, err, messages);
            return;
        }
        const encryptedPassphrase = yield call(symmetricEncrypt, passphrase, pcHash);
        const { encryptedBackupCode, encryptedRecoveryCode, encryptedRecoveryHash } = yield call(KeysService.generateRecoveryAndBackupCodes, pcHash);
        yield call(sendSecrets, {
            encryptedPassphrase,
            encryptedBackupCode,
            encryptedRecoveryCode,
            encryptedRecoveryHash
        });
        yield put(actions.passphraseRecoverySuccess(passphrase));
        yield call(returnRedirect);
    } catch (err) {
        yield put(actions.passphraseRecoveryError(err));
        yield call(App.dispatchError, err, messages);
    }
}
//----------------------------------------------------------------------------------------------------------------------
function* getPCHash() {
    const tokenData = yield call(OidcService.getTokenData);
    const hash = get(tokenData, 'pc_hash');
    if (!hash) {
        throw new LocalError({
            code: 'NoPCHash'
        });
    }
    return hash;
}
function* checkPassword(password) {
    const information = yield select(Information.selectors.information);
    const { email } = information || {};
    const hash = yield call(generateHash, password, email);
    const pcHash = yield call(getPCHash);
    if (hash !== pcHash) {
        throw new LocalError({
            code: 'WrongPassword'
        });
    }
}
function* elevatePermissions({ payload }) {
    try {
        const { password } = payload;
        yield call(checkPassword, password);
        yield put(actions.elevatePermissionsSuccess());
        yield delay(3 * 60 * 1000);
        yield put(actions.elevatePermissionsTimeout());
    } catch (err) {
        yield put(actions.elevatePermissionsError(err));
        yield call(App.dispatchError, err, messages);
    }
}
//----------------------------------------------------------------------------------------------------------------------
function* fetchOrganizationMemberships() {
    try {
        const requestUrl = '/api/Account/OrganizationMemberships';
        let organizationMemberships = yield call(ApiService.regionalRequest, requestUrl);
        if (organizationMemberships && organizationMemberships.length) {
            organizationMemberships = sortBy(reject(organizationMemberships, {
                status: 'Invitation'
            }), 'organization.name');
        }
        yield put(actions.fetchOrganizationMembershipsSuccess(organizationMemberships));
    } catch (err) {
        yield put(actions.fetchOrganizationMembershipsError(err));
        yield call(App.dispatchError, err, messages);
    }
}
function* approveOrganizationMembership({ payload }) {
    const { organizationMembership } = payload;
    try {
        const organizationUID = get(organizationMembership, 'organizationUID');
        const requestUrl = '/api/OrganizationMembership/approve';
        const approvedOrganizationMembership = yield call(ApiService.regionalRequest, requestUrl, {
            method: 'POST',
            body: {
                organizationUID
            }
        });
        yield put(actions.approveOrganizationMembershipSuccess(approvedOrganizationMembership));
    } catch (err) {
        yield put(actions.approveOrganizationMembershipError(err, organizationMembership));
        yield call(App.dispatchError, err, messages);
    }
}
function* rejectOrganizationMembership({ payload }) {
    const { organizationMembership } = payload;
    try {
        const organizationUID = get(organizationMembership, 'organizationUID');
        const requestUrl = '/api/OrganizationMembership/reject';
        yield call(ApiService.regionalRequest, requestUrl, {
            method: 'POST',
            body: {
                organizationUID
            }
        });
        yield put(actions.rejectOrganizationMembershipSuccess(organizationMembership));
    } catch (err) {
        yield put(actions.rejectOrganizationMembershipError(err, organizationMembership));
        yield call(App.dispatchError, err, messages);
    }
}
//----------------------------------------------------------------------------------------------------------------------
function* goToApp({ payload }) {
    try {
        const { app } = payload;
        const bls = yield getContext('bls');
        if (bls[app]) {
            const externalRedirect = yield getContext('externalRedirect');
            externalRedirect(bls[app]);
        }
    } catch (err) {
        yield call(App.dispatchError, err, messages);
    }
}
//----------------------------------------------------------------------------------------------------------------------
function* deleteAccount({ payload }) {
    try {
        const { password } = payload;
        yield delay(150); // allows UI to prepare before heavy checkPassword
        yield call(checkPassword, password);
        const requestUrl = '/api/Account';
        yield call(ApiService.regionalRequest, requestUrl, {
            method: 'DELETE'
        });
        const cookies = yield getContext('cookies');
        cookies.set('logoutStatus', 'accountDeleted', {
            path: '/'
        });
        yield put(actions.signOut());
    } catch (err) {
        yield put(actions.deleteAccountError(err));
        yield call(dispatchError, err, messages);
    }
}
//----------------------------------------------------------------------------------------------------------------------
function* editGuardianEmail() {
    try {
        // const { email } = payload;
        yield delay(10);
        // TODO: To implement
        // const requestUrl = '/api/Account';
        // yield call(ApiService.regionalRequest, requestUrl, {
        //   method: 'PUT',
        // });
        yield put(actions.editGuardianEmailSuccess());
    } catch (err) {
        yield put(actions.editGuardianEmail(err));
        yield call(dispatchError, err, messages);
    }
}
function* checkConfirmTermsOfServicesByGuardian() {
    try {
        yield delay(1000);
        // TODO: To implement
        // const requestUrl = '/api/Account';
        // yield call(ApiService.regionalRequest, requestUrl, {
        //   method: 'PUT',
        // });
        yield put(actions.checkConfirmTermsOfServicesByGuardianError());
    // yield put(actions.checkConfirmTermsOfServicesByGuardianSuccess());
    } catch (err) {
        yield put(actions.checkConfirmTermsOfServicesByGuardianError(err));
        yield call(dispatchError, err, messages);
    }
}
//----------------------------------------------------------------------------------------------------------------------
function* test() {
    /* eslint-disable no-console */ try {
        console.log('test start');
        const user = yield call(OidcService.getUser);
        console.log('USER', user);
        const account = yield call(getAccount);
        console.log('account', account);
        console.log('test end');
    } catch (err) {
        console.error('error', err);
    }
/* eslint-enable no-console */ }
//----------------------------------------------------------------------------------------------------------------------
function* sagas() {
    yield takeLatest(actionTypes.GET_ME, getMe);
    yield takeLatest(actionTypes.LIST_WALLET_ENTRIES, listWalletEntries);
    yield takeEvery(actionTypes.INIT_AUTH, initAuth);
    yield takeLatest(actionTypes.SIGN_IN, signIn);
    yield takeLatest(actionTypes.RESEND_OTP, resendOtp);
    // yield takeLatest(actionTypes.SIGN_IN_CALLBACK, signInCallback);
    yield takeLatest(actionTypes.SIGN_OUT, signOut);
    yield takeLatest(actionTypes.SIGN_OUT_CALLBACK, signOutCallback);
    yield takeLatest(actionTypes.SIGN_UP, signUp);
    yield takeLatest(actionTypes.FETCH_IDENTITY_PROVIDERS, fetchIdentityProviders);
    yield takeLatest(actionTypes.FETCH_IDENTITIES, fetchIdentities);
    yield takeLatest(actionTypes.SWITCH_IDENTITY, switchIdentity);
    yield takeLatest(actionTypes.SEND_EMAIL_CONFIRM, sendConfirmEmail);
    yield takeLatest(actionTypes.UPDATE_ACCOUNT_SETTINGS, updateAccountSettings);
    yield takeLatest(actionTypes.SET_INVITATION, setInvitation);
    yield takeLatest(actionTypes.ACCEPT_INVITATION, acceptInvitation);
    yield takeLatest(actionTypes.ACCEPT_INVITATION_WITH_NEW_PROFILE, acceptInvitationWithNewProfile);
    yield takeLatest(actionTypes.REJECT_INVITATION, rejectInvitation);
    yield takeLatest(actionTypes.FETCH_INVITATION_INFORMATION_TEMPLATE, fetchInvitationInformationTemplate);
    yield takeLatest(actionTypes.CHANGE_PASSWORD, changePassword);
    yield takeLatest(actionTypes.RESET_PASSWORD, resetPassword);
    yield takeLatest(actionTypes.SET_NEW_PASSWORD, setNewPassword);
    yield takeLatest(actionTypes.SET_NEW_PASSWORD_CC, setNewPasswordCC);
    yield takeLatest(actionTypes.PASSPHRASE_RECOVERY, passphraseRecovery);
    yield takeLatest(actionTypes.ELEVATE_PERMISSIONS, elevatePermissions);
    yield takeLatest(actionTypes.FETCH_ORGANIZATION_MEMBERSHIPS, fetchOrganizationMemberships);
    yield takeEvery(actionTypes.APPROVE_ORGANIZATION_MEMBERSHIP, approveOrganizationMembership);
    yield takeEvery(actionTypes.REJECT_ORGANIZATION_MEMBERSHIP, rejectOrganizationMembership);
    yield takeLatest(actionTypes.GO_TO_APP, goToApp);
    yield takeLatest(actionTypes.DELETE_ACCOUNT, deleteAccount);
    yield takeLatest(actionTypes.EDIT_GUARDIAN_EMAIL, editGuardianEmail);
    yield takeLatest(actionTypes.CHECK_CONFIRM_TERMS_OF_SERVICES_BY_GUARDIAN, checkConfirmTermsOfServicesByGuardian);
    yield takeLatest(actionTypes.TEST, test);
}
export default [
    sagas
];
